import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AdminChallengesService } from '@app/services/admin-challenges/admin-challenges.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { AdminThemeService } from '@app/services/admin-theme/admin-theme.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import {
  RangeSelectComponent,
  RangeSelection,
} from '@app/shared/components/range-select/range-select.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';

import { AccordionComponent } from '@app/components/accordion';
import {
  CardData,
  CardDataGridComponent,
} from '@app/components/card-data-grid';
import { HeadingComponent } from '@app/components/heading';
import { InputComponent, InputIcon } from '@app/components/input';
import { SpinnerComponent } from '@app/components/spinner';
import {
  GroupChallenge,
  GroupChallengeFilter,
  GroupChallengeSortColumn,
  GroupChallengeSortOrder,
  TenantTheme,
} from '@skyfit/core-data-library';

@Component({
  selector: 'app-challenges-select',
  templateUrl: './challenges-select.component.html',
  styleUrl: './challenges-select.component.css',
  standalone: true,
  imports: [
    CommonModule,
    TranslateDirective,
    TranslatePipe,
    CardDataGridComponent,
    HeadingComponent,
    InputComponent,
    SpinnerComponent,
    AccordionComponent,
    RangeSelectComponent,
  ],
})
export class ChallengesSelectComponent implements OnInit {
  private readonly challengesService = inject(AdminChallengesService);
  private readonly translationService = inject(TranslatorService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  private readonly adminThemeService = inject(AdminThemeService);
  private readonly adminTenantService = inject(AdminTenantService);
  isLoading = signal(true);
  theme?: TenantTheme;
  searchInput = signal('');
  readonly cards = signal<CardData<GroupChallenge>[]>([]);
  minLength = 0;
  maxLength = 21;
  InputIcon = InputIcon;

  ngOnInit(): void {
    this.onSearch();
    this.theme = this.adminThemeService.getCurrentTheme();
  }

  onLengthChange(range: RangeSelection): void {
    this.minLength = range.start;
    this.maxLength = range.end;
    this.onSearch();
  }

  onLengthInput(range: RangeSelection): void {
    this.minLength = range.start;
    this.maxLength = range.end;
  }

  onSearch(): void {
    this.isLoading.set(true);

    const filter: GroupChallengeFilter = {
      isShareable: true,
      sortColumn: GroupChallengeSortColumn.name,
      sortOrder: GroupChallengeSortOrder.ascendingNullsLast,
      ...(this.searchInput() && { name: this.searchInput() }),
      ...(this.minLength > 0 &&
        this.minLength < 21 && { minLength: this.minLength }),
      ...(this.maxLength > 0 &&
        this.maxLength < 21 && { maxLength: this.maxLength }),
    };

    const hasSearch = filter.name || filter.minLength || filter.maxLength;

    this.challengesService
      .searchChallenges(filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        /**
         * Here is the question: will we get the Challenge data here from the backend
         * with all the others.
         */
        next: (response) => {
          const challenges = response.groupChallenges;
          const cards: CardData<GroupChallenge>[] = hasSearch
            ? []
            : [...this.getDefaultChallenges()];
          challenges.forEach((challenge) => {
            cards.push({
              type: 'groupChallenge',
              backgroundColor:
                this.theme?.light?.colors?.[0]?.value ?? '#1100DB',
              title: challenge.title,
              subtitle: this.challengesService.formatDuration(
                challenge?.durationInDays
              ),
              data: challenge,
              imageUrl: challenge.imageUrl,
            });
          });
          this.cards.set(cards);
          this.isLoading.set(false);
        },
        error: (e) => {
          console.error(e);
          this.cards.set([]);
          this.isLoading.set(false);
        },
      });
  }

  backgroundUrl(url: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(`url(${url})`);
  }

  onChallengeSelected(cardData: CardData<GroupChallenge>): void {
    if (!cardData.data && cardData.type === 'upcomingChallenge') {
      return;
    }

    const hasMetricChallenges =
      this.adminTenantService.hasFeature('metricChallenges');
    let routeTo = <string[]>[];
    if (cardData.type === 'stepChallenge' && hasMetricChallenges) {
      routeTo = ['challenges', 'create', 'steps'];
    }

    if (cardData.type === 'groupChallenge') {
      const id = this.challengesService.encodeChallengeId(
        cardData.data.groupChallengeId
      );
      routeTo = ['challenges', 'select', id];
    }
    this.router.navigate(routeTo);
  }

  goBack(): void {
    this.location.back();
  }

  private getDefaultChallenges(): CardData<GroupChallenge>[] {
    const tag = this.translationService.translate('challenges.comingSoon');
    const hasMetricChallenges =
      this.adminTenantService.hasFeature('metricChallenges');
    const stepChallengeTag = hasMetricChallenges
      ? this.translationService.translate('challenges.new')
      : tag;
    const stepChallengeColor = hasMetricChallenges ? '#FFD600' : '#14E25A';

    return [
      {
        type: 'stepChallenge',
        title: this.translationService.translate('challenges.steps'),
        tag: stepChallengeTag,
        tagColor: stepChallengeColor,
        imageUrl: '/assets/img/challenge_steps.png',
      },
      {
        type: 'upcomingChallenge',
        title: this.translationService.translate('challenges.time'),
        tag,
        tagColor: '#14E25A',
        imageUrl: '/assets/img/challenge_time.png',
      },
      {
        type: 'upcomingChallenge',
        title: this.translationService.translate('challenges.distance'),
        tag,
        tagColor: '#14E25A',
        imageUrl: '/assets/img/challenge_distance.png',
      },
      {
        type: 'upcomingChallenge',
        title: this.translationService.translate('challenges.calories'),
        tag,
        tagColor: '#14E25A',
        imageUrl: '/assets/img/challenge_calories.png',
      },
    ];
  }
}
